import { createFileRoute } from "@tanstack/react-router";
import * as z from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  BarStack,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarIcon } from "lucide-react";
import { PopoverClose } from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { endOfToday, startOfToday, subMonths, subWeeks } from "date-fns";
import type { ChartConfig } from "@/components/ui/chart";
import type { DateRange } from "react-day-picker";
import type { GroupedPiiType } from "@/lib/queries/stats";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  piiStatsByTypeQueryOptions,
  piiStatsByUserQueryOptions,
  piiStatsQueryOptions,
} from "@/lib/queries/stats";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const range = z.discriminatedUnion("period", [
  z.object({
    period: z.enum(["today", "week", "month"]),
  }),
  z.object({
    period: z.literal("custom"),
    from: z.coerce.date(),
    to: z.coerce.date(),
  }),
]);

const analyticsSearchSchema = z.object({
  range: range
    .default({
      period: "week",
    })
    .catch({
      period: "week",
    }),

  topPiisBy: z.enum(["user", "pii"]).default("user").catch("user"),
});

export const Route = createFileRoute("/_app/analytics")({
  component: RouteComponent,
  validateSearch: zodValidator(analyticsSearchSchema),
});

function RouteComponent() {
  const { topPiisBy } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <div className="w-full space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold">Analytics</h1>
        <TimeRangeFilter />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily PII Detection Trends</CardTitle>
          <CardDescription>
            Total PII entities and critical PIIs detected over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StackedAreaChartComponent />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>PII Types Detected</CardTitle>
          <CardDescription>
            Count of detected PII entities by{" "}
            {topPiisBy === "pii" ? "type" : "user"}
          </CardDescription>
          <CardAction>
            <Tabs
              value={topPiisBy}
              onValueChange={(v) =>
                navigate({
                  to: ".",
                  search: (s) => ({
                    ...s,
                    topPiisBy: v as "user" | "pii",
                  }),
                  resetScroll: false,
                })
              }
            >
              <TabsList className="h-10">
                <TabsTrigger className="px-4 py-2" value="user">
                  User
                </TabsTrigger>
                <TabsTrigger className="px-4 py-2" value="pii">
                  PII type
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardAction>
        </CardHeader>
        <CardContent>
          <DetectedPissChart groupBy={topPiisBy} />
        </CardContent>
      </Card>
    </div>
  );
}

const rangeButtons: Array<{
  label: string;
  value: typeof Route.types.searchSchema.range.period;
}> = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "Custom", value: "custom" },
];
export function TimeRangeFilter() {
  const { range } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-1.5">
      {rangeButtons.map((button) => {
        if (button.value === "custom") {
          return (
            <Popover
              key={button.value}
              open={isCalendarOpen}
              onOpenChange={setIsCalendarOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant={range.period === "custom" ? "default" : "ghost"}
                  className="h-9 gap-2 rounded-md px-3 font-medium transition-all"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {button.label}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-6" align="start">
                <div className="space-y-4">
                  <Calendar
                    mode="range"
                    defaultMonth={new Date()}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                  />

                  <PopoverClose asChild>
                    <Button
                      disabled={!dateRange || !dateRange.to || !dateRange.from}
                      onClick={() => {
                        if (!dateRange || !dateRange.to || !dateRange.from)
                          return;
                        else
                          navigate({
                            to: ".",
                            search: (s) => ({
                              ...s,
                              range: {
                                period: "custom",
                                from: dateRange.from!,
                                to: dateRange.to!,
                              },
                            }),
                          });
                      }}
                      className="h-10 w-full rounded-lg font-medium shadow-sm"
                    >
                      Apply Custom Range
                    </Button>
                  </PopoverClose>
                </div>
              </PopoverContent>
            </Popover>
          );
        } else
          return (
            <Button
              key={button.value}
              variant={range.period === button.value ? "default" : "ghost"}
              onClick={() =>
                navigate({
                  to: ".",
                  search: (s) => ({
                    ...s,
                    range: { period: button.value as any },
                  }),
                })
              }
              className="h-9 rounded-md px-3 font-medium transition-all"
            >
              {button.label}
            </Button>
          );
      })}
    </div>
  );
}

const analyticsChartConfig = {
  totalPii: {
    label: "Total PII",
    color: "#B3B8DB",
  },
  criticalPii: {
    label: "Critical PII",
    color: "#F04438",
  },
} satisfies ChartConfig;

function DetectedPissChart({
  groupBy,
}: {
  groupBy: z.infer<typeof analyticsSearchSchema>["topPiisBy"];
}) {
  const { range } = Route.useSearch();
  const dateRange = useMemo((): { from: Date; to: Date } => {
    const now = new Date();

    switch (range.period) {
      case "today": {
        return { from: startOfToday(), to: endOfToday() };
      }
      case "week": {
        return { from: subWeeks(now, 1), to: now };
      }
      case "month": {
        return { from: subMonths(now, 1), to: now };
      }
      case "custom": {
        return range;
      }
    }
  }, [range]);

  const piiStatsByUser = useQuery({
    ...piiStatsByUserQueryOptions({
      start_date: dateRange.from,
      end_date: dateRange.to,
    }),
    enabled: groupBy === "user",
  });

  const piiStatsByType = useQuery({
    ...piiStatsByTypeQueryOptions({
      start_date: dateRange.from,
      end_date: dateRange.to,
    }),
    enabled: groupBy === "pii",
  });

  const query = groupBy === "user" ? piiStatsByUser : piiStatsByType;

  if (query.isLoading) {
    return <Skeleton className="h-125 w-full" />;
  }

  if (query.isError) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        Error loading data
      </div>
    );
  }

  return (
    <ChartContainer config={analyticsChartConfig} className="h-125 w-full">
      <BarChart data={query.data} className="[&>svg]:overflow-visible">
        <Tooltip
          content={
            <ChartTooltipContent
              className="w-40"
              formatter={(_value, name, _item, _index, payload) => {
                // juggling wrong type definitions
                const n = name as keyof typeof analyticsChartConfig;
                const p = payload as unknown as GroupedPiiType;

                const conf = analyticsChartConfig[n];

                return (
                  <>
                    <div
                      className={cn(
                        "shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
                        "h-2.5 w-2.5",
                      )}
                      style={
                        {
                          "--color-bg": conf.color,
                          "--color-border": conf.color,
                        } as React.CSSProperties
                      }
                    />
                    <div
                      className={cn(
                        "flex flex-1 justify-between leading-none",
                        "items-center",
                      )}
                    >
                      <div className="grid gap-1.5">
                        <span className="text-muted-foreground">
                          {conf.label}
                        </span>
                      </div>

                      <span className="font-mono font-medium text-foreground tabular-nums">
                        {p[n].toLocaleString()}
                      </span>
                    </div>
                  </>
                );
              }}
            />
          }
          cursor={false}
        />
        ˘
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={cartesianGridStroke}
          opacity={0.3}
        />
        <XAxis
          dataKey="name"
          stroke={axisStroke}
          fill={axisStroke}
          fontSize={12}
          tickLine={false}
          angle={-60}
          textAnchor="end"
          height={120}
          dy={10}
        />
        <YAxis
          stroke={axisStroke}
          fontSize={12}
          tickLine={false}
          tick={{ fill: axisStroke }}
        />
        <BarStack radius={[15, 15, 0, 0]}>
          <Bar
            stackId="pii"
            fill="var(--color-criticalPii)"
            dataKey="criticalPii"
          />
          <Bar
            stackId="pii"
            fill="var(--color-totalPii)"
            dataKey={(d: GroupedPiiType) => d.totalPii - d.criticalPii}
            name="totalPii"
          />
        </BarStack>
      </BarChart>
    </ChartContainer>
  );
}

const cartesianGridStroke = "#374151";
const axisStroke = "#9ca3af";
function StackedAreaChartComponent() {
  const { range } = Route.useSearch();

  // Calculate the current date range based on selection
  const dateRange = useMemo((): { from: Date; to: Date } => {
    const now = new Date();

    switch (range.period) {
      case "today": {
        return { from: startOfToday(), to: endOfToday() };
      }
      case "week": {
        return { from: subWeeks(now, 1), to: now };
      }
      case "month": {
        return { from: subMonths(now, 1), to: now };
      }
      case "custom": {
        return range;
      }
    }
  }, [range]);
  // Fetch PII stats from API
  const piiStats = useQuery({
    ...piiStatsQueryOptions({
      start_date: dateRange.from,
      end_date: dateRange.to,
    }),
    select: (data) =>
      data.time_series.map((item) => ({
        date: new Date(item.timestamp).toLocaleDateString(undefined, {
          month: "short",
          day: "2-digit",
        }),
        totalPii: item.total_pii_count,
        criticalPii: item.critical_pii_count,
      })),
  });

  const { current: randId } = useRef(crypto.randomUUID());

  if (piiStats.isLoading) {
    return <Skeleton className="h-125 w-full" />;
  }

  if (piiStats.isError) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        Error loading data
      </div>
    );
  }

  return (
    <ChartContainer className="h-125 w-full" config={analyticsChartConfig}>
      <AreaChart
        data={piiStats.data}
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        style={{ cursor: "default" }}
      >
        <defs>
          <linearGradient
            id={`colorTotalPII-${randId}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="5%"
              stopColor="var(--color-totalPii)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-totalPii)"
              stopOpacity={0.1}
            />
          </linearGradient>
          <linearGradient
            id={`colorCriticalPII-${randId}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="5%"
              stopColor="var(--color-criticalPii)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-criticalPii)"
              stopOpacity={0.1}
            />
          </linearGradient>
        </defs>
        <Tooltip content={<ChartTooltipContent />} cursor={false} />

        <CartesianGrid
          strokeDasharray="3 3"
          stroke={cartesianGridStroke}
          opacity={0.3}
        />
        <XAxis
          dataKey="date"
          stroke={axisStroke}
          fontSize={12}
          tickLine={false}
          tick={{ fill: axisStroke }}
        />
        <YAxis
          stroke={axisStroke}
          fontSize={12}
          tickLine={false}
          tick={{ fill: axisStroke }}
        />
        <Area
          type="monotone"
          dataKey="criticalPII"
          stackId="1"
          stroke="var(--color-criticalPii)"
          fill={`url(#colorCriticalPII-${randId})`}
          strokeWidth={2}
          animationDuration={800}
          name="Critical Pii"
        />
        <Area
          type="monotone"
          dataKey="totalPii"
          stackId="1"
          stroke="var(--color-totalPii)"
          fill={`url(#colorTotalPII-${randId})`}
          strokeWidth={2}
          animationDuration={800}
          name="Total PII"
        />
      </AreaChart>
    </ChartContainer>
  );
}
